// AddFamilyMember.js
import React, { useEffect, useState } from "react";
import { io } from "socket.io-client";
import { Typography } from "@mui/material";

import {
  addPatient,
  addRelatedPatient,
  createAppointment,
  getPeopleAheadCount,
} from "../services/patientRegistrationRequesApi";
import { useLocation, useNavigate } from "react-router-dom";
import InputComponent from "../components/InputComponent";
import { convertToNumber, getValueFromPatToken } from "../utils/commonUtils";
import { showToast } from "../../clinic/utils/toastUtils";
import { ToastContainer } from "react-toastify";
import * as Yup from "yup";
import { patientValidation } from "../utils/validations/patientValidation";
import CustomSelect from "../../clinic/components/CustomSelect";
import { calculateAgeR, calculateDateOfBirthFromAgeR } from "../../clinic/utils/commonUtils";
import AntDatePicker from "../../clinic/components/antDatepicker";
import dayjs from "dayjs";

const AddFamilyMember = () => {
  const location = useLocation();
  const preViousState = location?.state || {};
  const {
    clinicIdProps = null,
    doctorIdProps = null,
    memberAdd,
    relativePatId,
    ...restStateData
  } = location?.state ?? {};
  const [contactNumber, setContactNumber] = useState("");
  let number;
  useEffect(() => {
    (async () => {
      number = await localStorage.getItem("number");
      setContactNumber(number);
    })();
  }, []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [age, setAge] = useState("");
  const [date_of_birth, setBirthDate] = useState("");

  const [clinicId, setClinicId] = useState();
  const [doctorId, setDoctorId] = useState(null);
  const [appointmentNumber, setAppointmentNumber] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [peopleAhead, setPeopleAhead] = useState(null);
  const [gender, setGender] = useState("");
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [patientId, setPatientId] = useState();
  const [relativePatientId, setRelativePatientId] = useState(0);
  const [familyMemberAddOnly, setFamilyMemberAddOnly] = useState(null);

  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState({});
  const [userId, setUserId] = useState();
  const [addRelativeClicked, setAddRelativeClicked] = useState(false);

  const genderOptions = [
    { value: "selectgender", label: "Select Gender" },
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const isNumeric = (value) => {
    return /^\d+$/.test(value);
  };

  const navigate = useNavigate();

  const allFieldsFilled = () => {
    return (
      firstName && lastName && age && contactNumber && gender
      // && weight
    );
  };
  useEffect(() => {
    let userIdget = getValueFromPatToken("user_id");
    setUserId(userIdget);
  }, []);

  useEffect(() => {
    setClinicId(clinicIdProps);
    setDoctorId(doctorIdProps);
    setAddRelativeClicked(memberAdd);
    setRelativePatientId(relativePatId);
  }, []);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL);

    // Listen for 'queueUpdated' event
    socket.on("queueUpdated", async (data) => {
      // Check if the current user's clinicId and doctorId match the emitted event data
      if (
        Number(data.clinic_id) === Number(clinicId) &&
        Number(data.doctor_id) === Number(doctorId)
      ) {
        // Fetch the updated peopleAheadCount using the updated patientId
        try {
          const response = await getPeopleAheadCount(
            patientId,
            doctorId,
            clinicId
          );
          if (response?.status === 200 && response.data.count !== undefined) {
            setPeopleAhead(response.data.count);
          } else {
            console.error("Failed to get people ahead count");
          }
        } catch (error) {
          console.error(
            "An error occurred while fetching people ahead count:",
            error
          );
        }
      }
    });

    return () => {
      socket.off("queueUpdated");
      socket.disconnect();
    };
  }, [doctorId, clinicId, patientId]); // Include patientId in the dependency array

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsRegistering(true);
    setErrorMessage({});

    try {
      await patientValidation.validate(
        {
          firstName,
          lastName,
          age,
		  date_of_birth,
          contactNumber,
          gender,
        },
        { abortEarly: false }
      );

      // If the form data is valid, proceed with the submission
      if (!addRelativeClicked) {
        try {
          const response = await addPatient(
            userId,
            firstName,
            lastName,
            age,
			date_of_birth,
            gender
          );

          if (response?.status) {
            const patientId = response?.data?.patientId;
            const appointmentResponse = await createAppointment(
              convertToNumber(patientId),
              convertToNumber(doctorId),
              convertToNumber(clinicId)
            );

            if (clinicIdProps && doctorIdProps) {
              setAppointmentNumber(appointmentResponse.data.appointment_number);
              setPatientId(appointmentResponse.data.patient_id);

              localStorage.setItem("clinicId", clinicId);
              localStorage.setItem("doctorId", doctorId);
              localStorage.setItem(
                "patientId",
                appointmentResponse.data.patient_id
              );
              localStorage.setItem(
                "appointmentNumber",
                appointmentResponse.data.appointment_number
              );

              showToast(
                "Patient added successfully and confirmation of your appointment",
                "success"
              );
              setTimeout(() => {
                navigate("/patient/appointmentConfirmation", {
                  state: { ...preViousState },
                });
              }, 2000);
            } else {
              showToast("Patient added successfully", "success");
              setTimeout(() => {
                setFamilyMemberAddOnly(true);
                navigate("/patient/familylist");
              }, 2000);
            }
          } else {
            // Handle the scenario where the patient is not added
            console.log(response?.message);
          }
        } catch (error) {
          console.error("Network error:", error);
          setErrorMessage({ customError: "Network error. Please try again." });
        } finally {
          setIsRegistering(false);
        }
      } else {
        const relationType = "";
        try {
          const response = await addRelatedPatient(
            relativePatientId,
            firstName,
            lastName,
            age,
			date_of_birth,
            gender,
            contactNumber,
            relationType
          );

          if (response?.status) {
            const patientId = response.data.patientId;

            if (clinicIdProps && doctorIdProps) {
              const appointmentResponse = await createAppointment(
                convertToNumber(patientId),
                convertToNumber(doctorId),
                convertToNumber(clinicId)
              );
              setAppointmentNumber(appointmentResponse.data.appointment_number);
              setPatientId(appointmentResponse.data.patient_id);
              localStorage.setItem("clinicId", clinicId);
              localStorage.setItem("doctorId", doctorId);
              localStorage.setItem(
                "patientId",
                appointmentResponse.data.patient_id
              );
              localStorage.setItem(
                "appointmentNumber",
                appointmentResponse.data.appointment_number
              );

              showToast(
                "Patient added successfully and confirmation of your appointment",
                "success"
              );
              setTimeout(() => {
                navigate("/patient/familylist", {
                  state: { ...preViousState },
                });
              }, 2000);
            } else {
              showToast("Patient added successfully", "success");
              setTimeout(() => {
                navigate("/patient/familylist", {});
              }, 2000);
            }
          } else {
            // Handle the scenario where the patient is not added
          }
        } catch (error) {
          console.error("Network error:", error);
          setErrorMessage({ customError: "Network error. Please try again." });
        } finally {
          setIsRegistering(false);
        }
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        // Handle the validation error
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrorMessage(validationErrors);
      } else {
        console.error("Network error:", error);
        setErrorMessage({ customError: "Network error. Please try again." });
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleContactNumberChange = (event) => {
    const input = event.target.value;
    if (input === "" || (isNumeric(input) && input.length <= 10)) {
      setContactNumber(input);
    }
  };

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };

  const handleGenderChange = (event) => {
    setGender(event.value);
  };

  const handleAgeChange = (event) => {
    if (isNumeric(event.target.value)) {
      setAge(event.target.value);
    }
  };

  const CalculateAgeFromDate =(dateString)=>{
  if(dateString){
    setBirthDate(dayjs(dateString).startOf('day').format("YYYY-MM-DD"));
    let localAge = calculateAgeR(dateString);
    setAge(localAge);
    }
  }

  const handleChangeAge = (event)=>{
    if (isNaN(event.target.value)) {
      setAge("")
      showToast("Only number allowed","error")
    }else{
      if(event.target.value < 1){
        let localDob = calculateDateOfBirthFromAgeR(age);
        setBirthDate(dayjs(localDob).format('YYYY-MM-DD'));
        CalculateAgeFromDate(dayjs(localDob).format('YYYY-MM-DD'))
      }
      let localDob = calculateDateOfBirthFromAgeR(age)
      setBirthDate(dayjs(localDob).format('YYYY-MM-DD'));
    }
  } 

  return (
    <div className="flex flex-col  lg:justify-center items-center bg-[#f2f9ff] pt-5 xl:pt-0 justify-center min-h-[calc(100vh-120px)] w-full">
      <div className=" w-full md:w-8/12 lg:w-6/12 xl:w-4/12 px-5 md:px-3 xl:px-0">
        <div className=" md:bg-white md:shadow-sm md:px-5 py-5 lg:px-14 lg:py-12 rounded-2xl">
          <div className="mb-8">
            <h3 className="mb-1 text-xl font-bold text-[#04225b]">
              Patient Details
            </h3>
          </div>
          <form onSubmit={handleSubmit}>
            <InputComponent
              label="First Name"
              id="firstName"
              value={firstName}
              onChange={handleFirstNameChange}
              type="text"
              validationRequired={true}
              labelClassName={"bg-[#f2f9ff] md:bg-white"}
              inputClassName={"bg-[#f2f9ff] md:bg-white"}
              error={errorMessage?.firstName}
            />
            <InputComponent
              label="Last Name"
              id="lastName"
              value={lastName}
              onChange={handleLastNameChange}
              type="text"
              validationRequired={true}
              labelClassName={"bg-[#f2f9ff] md:bg-white"}
              inputClassName={"bg-[#f2f9ff] md:bg-white"}
              error={errorMessage?.lastName}
            />

            <div className="w-full lg:w-full mb-5">
              <label className="mb-1 text-gray-800 block font-semibold text-[14px] bg-[#f2f9ff] md:bg-white">
                Date of birth
				<sup className=" text-red-500">*</sup>
              </label>
              
              <AntDatePicker
                defaultValue={null}
                placeholder="select date"
                value={date_of_birth?dayjs(date_of_birth):''}
                onChange={(dateString) => CalculateAgeFromDate(dateString)}
                disabledFutureDate={true}
              />
			  {errorMessage?.date_of_birth != "" && <div className=" text-red-500 text-sm">{errorMessage?.date_of_birth}</div>}
            </div>

            <InputComponent
              label="Age"
              id="age"
              value={age}
              type="tel"
              //validationRequired={true}
              labelClassName={"bg-[#f2f9ff] md:bg-white"}
              inputClassName={"bg-[#f2f9ff] md:bg-white"}
              onChange={(e) => setAge(e.target.value)}
              onBlur={handleChangeAge}
              //error={errorMessage?.age}
            />
            {/* <InputComponent
              label="Weight"
              id="weight"
              value={weight}
              onChange={handleWeightChange}
              type="text"
              validationRequired={true}
              labelClassName={"bg-[#f2f9ff] md:bg-white"}
              inputClassName={"bg-[#f2f9ff] md:bg-white"}
            /> */}

            <InputComponent
              label="Contact Number"
              id="contactNumber"
              value={contactNumber}
              disabled={true}
              onChange={handleContactNumberChange}
              type="tel"
              validationRequired={true}
              labelClassName={"bg-[#f2f9ff] md:bg-white"}
              inputClassName={"bg-[#f2f9ff] md:bg-white"}
            />

            <label className="mb-1 text-gray-800 block font-semibold text-[14px] bg-[#f2f9ff] md:bg-white">
              Gender <sup className=" text-red-500">*</sup>
            </label>
            <CustomSelect
              id="gender"
              value={gender} // Pass the selected gender value
              onChange={handleGenderChange} // Handle the change event
              options={genderOptions}
              placeholder="Select gender"
              isSearchable={false}
              isClearable={false}
              className={" w-full form_select"}
            />

            <div className=" text-red-500 text-sm">{errorMessage?.gender}</div>
            <button
              type="submit"
              className="min-w-[131px] w-full text-base font-medium rounded-lg py-2 px-5 bg-[#0188fc] hover:bg-[#076ec7] text-white mt-5"
              disabled={isRegistering || registrationCompleted}
            >
              Register
            </button>
          </form>
          {/* {appointmentNumber && (
            <Card style={{ marginTop: 20 }}>
              <CardContent>
                <Typography variant="h5" component="div">
                  Appointment Number :
                </Typography>
                <Typography variant="h4" color="primary">
                  {appointmentNumber}
                </Typography>
                <Typography variant="body2">
                  Please wait for your turn
                </Typography>
              </CardContent>
            </Card>
          )} */}

          {familyMemberAddOnly && (
            <div className=" text-green-800 bg-green-50 p-3">
              Successfully add family member!
            </div>
          )}

          {peopleAhead != null && (
            <Typography variant="body2">
              {peopleAhead === 0
                ? "Your turn is coming up next!"
                : `${peopleAhead} people ahead of you.`}
            </Typography>
          )}
          <div className=" text-red-500">{errorMessage?.customError}</div>

          <ToastContainer />
        </div>
      </div>
    </div>
  );
};

export default AddFamilyMember;
